/* ae-cart.js (build r21, speed pass r28, V3 drawer r29): cart drawer actions + first-order code sync.
 *
 * Offer data: <script id="ae-offer-config"> (snippets/ae-offer-config.liquid).
 * - [data-ae-toggle]  one-time <-> subscription on the same line (/cart/change.js with
 *   selling_plan / null). Shopify changes the line in place and rejects an invalid plan with
 *   the line untouched, so a failed toggle never loses the item. r29: [data-ae-once] = the V3
 *   "One-time purchase for $70" switch: on = one-time (selling_plan null), off = data-plan.
 * - [data-ae-switch3] 1-month -> 3-month: r30b: in place (/cart/change.js quantity + selling_plan) when
 *   the 3-month offer is the line's own variant, then the code sync; else add the 3-month line first
 *   and remove the 1-month line only after the add succeeds.
 * - [data-ae-switch1] r30: adult 3-month -> 1-month on the same line (/cart/change.js with quantity
 *   data-qty + selling_plan data-plan, in place: Shopify rejects a bad change with the line untouched),
 *   then the same code sync as every action (FIRST15 back on for the 1-month line).
 * - [data-ae-addkids] add the kids subscription.
 * - sync(): while first_order_code is set, keep it on the cart whenever an adult line on a
 *   first-order plan (config first_order_plan_ids; r25: the 1-month plan only) is present AND no
 *   adult 3-month line is (40% + 15% must never stack: Andrew 2026-10-01), and take it off
 *   otherwise. The cart `discount` param replaces any other code, e.g. WELCOME10. Does nothing
 *   while the code is empty.
 * - checkBands(): the "New customer N% off (first order only)" total row (r29; the green band
 *   before) states only what /cart.js says the cart applied: the row's code (= first_order_code)
 *   must be an applicable discount code with an allocated amount; its text and amount are rebuilt
 *   from that application. Anything else removes the row and re-renders the drawer once.
 *
 * r28 (2026-10-01, Andrew: the drawer took ~4 s to slide out after the PDP add):
 * - addAndOpen(): the PDP add (assets/ae-pdp4.js). The drawer opens on the tap with a loading
 *   state (skeleton line, totals hidden). The code change the add will need is worked out from
 *   the last known cart (prefetched at idle) BEFORE the add: /cart/update.js {discount} first,
 *   then /cart/add.js with `sections`, so the add returns the drawer already priced with the code.
 *   One render, no /?section_id= GET. A background /cart.js read then checks the band, the counts
 *   and the code (sync), and re-renders only if the prediction was wrong.
 * - every drawer action and cart:refresh render from the `sections` the cart call returns
 *   (or one section GET run in parallel with the /cart.js read), never a second full refresh.
 * - warm(): at idle, the drawer's fonts and line images are fetched, so the first render paints.
 * r29: prep(): once a PDP buy button has been on screen, the first-order code is already where the
 *   picked plan needs it, so the add is one request (see prep below).
 * r30b (live bug 2026-10-02, Andrew's drawer: 3-month qty 3 at $107.10 "Save 50% ongoing"): prep() parks
 *   FIRST15 on the cart while 1-month is picked, and every sync here returned early while the config's
 *   first_order_code was '' (12:11-12:40 PT), so a parked FIRST15 rode onto a 3-month line. Now:
 *   - guard codes (config first_order_guard_codes + the live code) come off any cart with an adult
 *     3-month line on EVERY path, whether the offer is on or off (wanted(): blocked first);
 *   - page load: a server-rendered drawer with an adult 3-month line reads the cart once and fixes it
 *     (audit), so no page starts from 40% + 15%;
 *   - the theme's quantity / remove (CartItems.updateQuantity, /cart/change.js cannot carry a code):
 *     the code change the new quantities need goes first, so the theme's one render is priced right;
 *   - drawer 1-month -> 3-month switches the line in place (/cart/change.js) + code off: 2 calls, 1 render;
 *   - line keys: Shopify re-keys every line when the codes change, so a drawer change by key that is
 *     rejected as "no valid id" is retried once with the line's current key (changeLine).
 *   - plans, not units (Andrew 2026-10-02): a 3-month line keeps 3 units per plan in the cart, but every
 *     count (header + drawer badge, sticky cart button) and the drawer stepper show plans (qty / 3);
 *     a stepper value is multiplied by its data-ae-plan-step before it goes to Shopify.
 *   Shopify (live carts 2026-10-02): /cart/update.js {discount: ''} removes every code; add.js and
 *   change.js ignore a `discount` field, so a code change is always its own update.js call.
 * Prices on screen are always Shopify's own render of the cart after the last change. */
(() => {
  if (window.AECart) return;

  const cfg = () => {
    try { return JSON.parse(document.getElementById('ae-offer-config').textContent); } catch (e) { return null; }
  };

  let mutations = 0; // r28: a /cart.js read that started before a cart change never overwrites what that change returned
  const post = async (url, body) => {
    mutations++;
    const r = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    let j = {};
    try { j = await r.json(); } catch (e) {}
    if (!r.ok || j.status === 422 || j.status === 'bad_request') {
      const err = new Error(j.description || j.message || 'Cart update failed');
      err.data = j;
      throw err;
    }
    return j;
  };

  /* r28: the last full cart seen (a /cart.js read, or an update.js / change.js response). */
  let known = null;
  const tried = new Set(); // one try to ADD the code per cart state (a rejected code cannot loop)
  const isCart = (c) => c && Array.isArray(c.items) && typeof c.item_count === 'number';
  // r30b: an emptied cart starts over: earlier one-try-per-cart-state code attempts are forgotten
  const remember = (c) => { if (isCart(c)) { known = c; if (c.item_count === 0) tried.clear(); } return c; };
  let lastRead = 0;
  const getCart = () => {
    const at = mutations;
    lastRead = Date.now();
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()).then((c) => (at === mutations ? remember(c) : c));
  };

  const refresh = (open = true) =>
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open, aeSynced: true } }));

  const drawer = () => document.querySelector('cart-drawer');
  const busy = (on) => { const d = drawer(); if (d) d.classList.toggle('is-ae-busy', !!on); };
  const sectionId = () => { const d = drawer(); return (d && d.dataset.sectionId) || ''; };
  const contentEl = () => { const sid = sectionId(); return sid ? document.getElementById('CartDrawer-' + sid) : null; };
  const withSections = (body) => {
    const sid = sectionId();
    return sid ? Object.assign({}, body, { sections: sid, sections_url: location.pathname }) : body;
  };
  const sectionHtml = (res) => { const sid = sectionId(); return (res && res.sections && sid && res.sections[sid]) || ''; };
  const rootUrl = () => ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/');

  const isAdult = (c, i) => i.handle === c.adult_handle || i.handle === c.three_month_handle;
  const planOf = (i) => Number(i.selling_plan_allocation && i.selling_plan_allocation.selling_plan && i.selling_plan_allocation.selling_plan.id) || 0;
  const foPlans = (c) => (Array.isArray(c.first_order_plan_ids) ? c.first_order_plan_ids : [c.plan_1m_id]).map(Number).filter(Boolean);
  /** r30b: the offer is on (the theme adds the code). */
  const foOn = (c) => !!(c && c.first_order_active && c.first_order_code);
  /** r30b: lower-case codes that must never sit on an adult 3-month cart: the live code + first_order_guard_codes. */
  const guardCodes = (c) => (c ? [c.first_order_code].concat(String(c.first_order_guard_codes || '').split(','))
    .map((x) => String(x || '').trim().toLowerCase()).filter(Boolean) : []);
  /** r30b: something to keep in step (the offer, or a guard code to take off 3-month carts). */
  const guarding = (c) => foOn(c) || guardCodes(c).length > 0;
  /** Plans of the adult lines (a line without `quantity` is a prospective one-line add). */
  const adultPlans = (c, items) => items.filter((i) => isAdult(c, i) && planOf(i) && (i.quantity == null || Number(i.quantity) > 0)).map(planOf);
  /** r25: an adult 3-month line (40%) blocks the first-order code, unless 3-month is itself a first-order plan. */
  const blocked = (c, items) => {
    const p3 = Number(c.plan_3m_id);
    return !!p3 && !foPlans(c).includes(p3) && adultPlans(c, items).includes(p3);
  };
  /** r25: an adult line on a first-order plan, and no adult 3-month line (unless 3-month is itself listed). */
  const foEligible = (c, items) => !blocked(c, items) && adultPlans(c, items).some((p) => foPlans(c).includes(p));
  /** The `discount` value the cart needs (string), or null when its codes are already right.
      items: the lines to judge (default: the cart's own), e.g. the cart plus a line about to be added. */
  function wanted(c, cart, items) {
    if (!guarding(c) || !cart) return null;
    const list = items || cart.items || [];
    const codes = (cart.discount_codes || []).map((d) => String(d.code));
    const guard = guardCodes(c);
    const isGuard = (x) => guard.includes(String(x).toLowerCase());
    // r30b: an adult 3-month line = no first-order code, on every path, offer on or off
    if (blocked(c, list)) return codes.some(isGuard) ? codes.filter((x) => !isGuard(x)).join(',') : null;
    if (!foOn(c)) return null;
    const lc = String(c.first_order_code).toLowerCase();
    const eligible = foEligible(c, list);
    const has = codes.some((x) => x.toLowerCase() === lc);
    if (eligible && !has) return String(c.first_order_code);
    if (!eligible && has) return codes.filter((x) => x.toLowerCase() !== lc).join(',');
    return null;
  }
  /** Same as wanted(), plus the one-attempt-per-cart-state guard, so a rejected code cannot loop. */
  function decide(cart) {
    const c = cfg();
    const next = wanted(c, cart);
    if (next === null) return null;
    if (foOn(c) && next === String(c.first_order_code)) {
      const key = [cart.token, cart.item_count, cart.total_price, next].join('|');
      if (tried.has(key)) return null;
      tried.add(key);
    } else {
      // r25: taking the code off (e.g. a 3-month line arrived) cannot loop; forget earlier
      // attempts so the code goes back on if the cart returns to an eligible state.
      tried.clear();
    }
    return next;
  }

  /** Returns true when it changed the cart's codes. */
  async function sync(cart) {
    const c = cfg();
    if (!guarding(c)) return false;
    cart = isCart(cart) ? cart : await getCart();
    const next = decide(cart);
    if (next === null) return false;
    remember(await post('/cart/update.js', { discount: next }));
    return true;
  }

  /* ---- first-order band: truth from /cart.js ---- */
  const money = (cents) => {
    const f = (window.FoxTheme && FoxTheme.settings && FoxTheme.settings.moneyFormat) || '${{amount}}';
    if (window.FoxTheme && FoxTheme.Currency && typeof FoxTheme.Currency.formatMoney === 'function') {
      try { return FoxTheme.Currency.formatMoney(cents, f); } catch (e) {}
    }
    return '$' + (Number(cents || 0) / 100).toFixed(2);
  };
  /** Every application of `code` on the cart: [{ value, value_type, amount }]. */
  function applied(cart, code) {
    const lc = String(code || '').toLowerCase();
    const out = [];
    if (!lc || !cart) return out;
    const ok = (cart.discount_codes || []).some((d) => String(d.code).toLowerCase() === lc && d.applicable !== false);
    if (!ok) return out;
    (cart.cart_level_discount_applications || []).forEach((a) => {
      if (String(a.title || '').toLowerCase() === lc) out.push({ value: Number(a.value), value_type: a.value_type, amount: Number(a.total_allocated_amount) || 0 });
    });
    (cart.items || []).forEach((i) => (i.line_level_discount_allocations || []).forEach((al) => {
      const a = al.discount_application || {};
      if (String(a.title || '').toLowerCase() === lc) out.push({ value: Number(a.value), value_type: a.value_type, amount: Number(al.amount) || 0 });
    }));
    return out;
  }
  /* r28: a render that already has the cart behind it hands it over (primed), so the band check
     right after that render reads no extra /cart.js. Taken synchronously by the next checkBands. */
  let bandRun = 0;
  let primed = null;
  let lastBandFix = 0;
  async function checkBands() {
    const pre = primed;
    primed = null;
    const bands = Array.prototype.filter.call(document.querySelectorAll('cart-drawer [data-ae-fo-band]'), (b) => !b.hasAttribute('data-ae-checked'));
    if (!bands.length) return;
    const run = ++bandRun;
    let cart = null;
    try { cart = await (pre || getCart()); } catch (e) { cart = null; }
    if (run !== bandRun) return;
    const c = cfg();
    const cfgCode = c && c.first_order_code ? String(c.first_order_code).toLowerCase() : '';
    bands.forEach((band) => {
      if (!band.isConnected) return;
      band.setAttribute('data-ae-checked', '');
      const code = String(band.getAttribute('data-code') || '');
      const apps = applied(cart, code);
      const amount = apps.reduce((n, a) => n + a.amount, 0);
      if (!code || (cfgCode && cfgCode !== code.toLowerCase()) || !apps.length || amount <= 0) {
        band.remove();
        // r29: the row is part of the totals, so a stale render is re-rendered (at most once per 10 s)
        if (Date.now() - lastBandFix > 10000) { lastBandFix = Date.now(); refresh(false); }
        return;
      }
      const pcts = apps.filter((a) => a.value_type === 'percentage').map((a) => a.value);
      const same = pcts.length === apps.length && pcts.every((v) => v === pcts[0]);
      const t = band.querySelector('[data-ae-fo-text]');
      const m = band.querySelector('[data-ae-fo-amt]');
      if (t) t.textContent = same ? 'New customer ' + (Math.round(pcts[0] * 100) / 100) + '% off' : 'New customer discount';
      if (m) m.textContent = '−' + money(amount);
    });
  }
  const watchBands = () => {
    const d = drawer();
    if (!d || d._aeBandWatch) return;
    d._aeBandWatch = new MutationObserver(() => { if (d.querySelector('[data-ae-fo-band]:not([data-ae-checked])')) checkBands(); else primed = null; });
    d._aeBandWatch.observe(d, { childList: true, subtree: true });
    checkBands();
  };

  /* ---- r28: counts (header bubbles + anything listening for ae:cart-count, e.g. the PDP sticky bar) ---- */
  /* r30b: in plans. Units in one 3-month plan: the plan's bags, over the bags in one unit of the line's
     variant (the main variant = 1 bag, so 3), the same rule as the drawer's ae_count and stepper. */
  const planUnits = (c, i) => {
    if (!c || planOf(i) !== Number(c.plan_3m_id)) return 1;
    const bags = Number(c.three_month_bags) || 1;
    const vid = Number(i.variant_id || i.id);
    const ub = vid === Number(c.three_month_variant_id) && vid !== Number(c.adult_variant_id)
      ? Math.max(1, Math.floor(bags / (Number(c.three_month_qty) || 1))) : 1;
    return Math.max(1, Math.floor(bags / ub));
  };
  /** The count to show for a full cart: a 3-month line = qty / plan size (rounded up). */
  const shownCount = (cart) => {
    const c = cfg();
    if (!cart || !Array.isArray(cart.items)) return Number(cart && cart.item_count);
    return cart.items.reduce((n, i) => n + Math.ceil((Number(i.quantity) || 0) / planUnits(c, i)), 0);
  };
  const shownCart = (cart) => (cart && Array.isArray(cart.items) && typeof cart.item_count === 'number'
    ? Object.assign({}, cart, { item_count: shownCount(cart) }) : cart);
  let lastCount = null;
  function counts(n, cart) {
    if (cart && Array.isArray(cart.items)) n = shownCount(cart);
    n = Number(n);
    if (!isFinite(n) || n < 0) return;
    if (cart) {
      document.querySelectorAll('cart-count').forEach((el) => {
        if (el.closest('cart-drawer')) return; // the drawer's own count comes with its render
        try { if (typeof el.onCartUpdate === 'function') el.onCartUpdate({ cart: shownCart(cart) }); } catch (e) {}
      });
    }
    if (n === lastCount) return;
    lastCount = n;
    document.dispatchEvent(new CustomEvent('ae:cart-count', { detail: { count: n } }));
  }
  const renderedCount = (el) => {
    const cc = el && el.querySelector('.ae-cart__title cart-count');
    if (!cc) return null;
    if (cc.hasAttribute('hidden')) return 0;
    const n = parseInt(cc.textContent, 10);
    return isFinite(n) ? n : null;
  };

  /* ---- r28: loading state + one render path ---- */
  /* r29: V3 layout (one line + the totals); `band` is kept for callers, the first-order row is in the totals. */
  const SKEL = () =>
    '<div class="ae-cart__skel-line"><span class="ae-cart__skel-img"></span><span class="ae-cart__skel-txt"><i></i><i></i><i></i><i></i><i></i></span></div>' +
    '<div class="ae-cart__skel-sum"><i></i><i></i><i></i><i></i><i></i><i></i></div>' +
    '<p class="visually-hidden" role="status">Adding to your cart…</p>';
  function setLoading(on, band) {
    const d = drawer();
    const el = contentEl();
    if (!d || !el) return;
    d.classList.toggle('is-ae-loading', !!on);
    const old = el.querySelector('.ae-cart__skel');
    if (on) {
      el.setAttribute('aria-busy', 'true');
      if (old) old.remove();
      const sk = document.createElement('div');
      sk.className = 'ae-cart__skel';
      sk.innerHTML = SKEL(band);
      const head = el.querySelector('.ae-cart__header');
      if (head) head.after(sk); else el.prepend(sk);
    } else {
      el.removeAttribute('aria-busy');
      if (old) old.remove();
    }
  }
  /** Open the drawer now, showing the loading state until render() lands. */
  function openLoading(focus, band) {
    const d = drawer();
    if (!d || !contentEl()) return false;
    setLoading(true, band);
    if (!d.open && typeof d.show === 'function') d.show(focus || null);
    return true;
  }
  /** Put a rendered cart-drawer section into the drawer. cartP: a promise of the cart behind it. */
  function render(html, cartP) {
    const el = contentEl();
    if (!el || !html) return false;
    const fresh = new DOMParser().parseFromString(html, 'text/html').getElementById(el.id);
    if (!fresh) return false;
    primed = cartP || null;
    el.innerHTML = fresh.innerHTML;
    // the band observer takes `primed` in its microtask (queued by the innerHTML change, so it runs first)
    queueMicrotask(() => { primed = null; });
    const d = drawer();
    if (d) d.classList.remove('is-ae-loading');
    el.removeAttribute('aria-busy');
    const n = renderedCount(el);
    if (n !== null) counts(n);
    return true;
  }
  const getSection = () =>
    fetch(rootUrl() + '?section_id=' + encodeURIComponent(sectionId()), { credentials: 'same-origin' }).then((r) => (r.ok ? r.text() : ''));

  /** cart:refresh (assets/cart.js): one round trip when the codes are right, two when they change. */
  async function refreshDrawer(detail) {
    detail = detail || {};
    const d = drawer();
    if (!d || !contentEl()) return;
    const c = cfg();
    let html = '';
    let cartP = null;
    if (!detail.aeSynced && guarding(c)) {
      const [cart, sec] = await Promise.all([getCart(), getSection()]);
      const next = decide(cart);
      if (next !== null) {
        const u = remember(await post('/cart/update.js', withSections({ discount: next })));
        html = sectionHtml(u);
        cartP = Promise.resolve(u);
      } else {
        html = sec;
        cartP = Promise.resolve(cart);
      }
    }
    if (!html) html = await getSection();
    if (!render(html, cartP)) throw new Error('cart drawer render failed');
    if (detail.open === true && !d.open) d.show();
  }

  /** Called by assets/cart.js after the theme's own quantity / remove updates (cart = change.js result). */
  async function afterUpdate(cart) {
    remember(cart);
    if (cart && typeof cart.item_count === 'number') counts(cart.item_count, cart);
    try {
      const c = cfg();
      if (!guarding(c)) return;
      const next = decide(isCart(cart) ? cart : await getCart());
      if (next === null) return;
      const u = remember(await post('/cart/update.js', withSections({ discount: next })));
      if (!render(sectionHtml(u), Promise.resolve(u))) refresh(false);
    } catch (e) { console.warn('ae-cart sync', e); }
  }

  /** Background check after a render that had no full cart behind it. */
  function verify(cartP) {
    cartP.then(async (cart) => {
      counts(cart.item_count, cart);
      try {
        const next = decide(cart);
        if (next === null) return;
        const u = remember(await post('/cart/update.js', withSections({ discount: next })));
        if (!render(sectionHtml(u), Promise.resolve(u))) refresh(false);
      } catch (e) { console.warn('ae-cart sync', e); }
    }).catch(() => {});
  }

  /** r28: the PDP add. item = { id, quantity, selling_plan? }; opts.focus = the element to return focus to.
      Resolves with the add.js response after the drawer shows the new cart; rejects when the add fails
      (the drawer closes again and the cart's codes are put back). */
  let prefetching = null;

  /* r29 (Andrew: "make sure the performance testing on it is good"; final drawer < 1.3 s on his
     phone): code prep. Once the PDP buy button (or the sticky one) has been on screen, the cart's
     first-order code is put where the add will need it for the plan the buy box has picked, with
     the same wanted() rule the add uses. The add is then ONE request (/cart/add.js with sections)
     instead of update.js + add.js in a row. Picking 3-month or one-time takes a prepped code off
     again before the tap (40% + 15% never stack). Only while the cart holds no other code (a
     lander's code is never swapped before the shopper adds); otherwise the add does it as in r28. */
  let prepChain = Promise.resolve();
  let prepBusy = 0;
  let prepBusyAdd = false; // r30b: an addAndOpen is waiting on the prep chain (its own render follows)
  let ctaSeen = false;
  const pickedLine = () => {
    const f = document.querySelector('[data-ae4-form]');
    if (!f) return null;
    const id = Number((f.querySelector('[data-ae4-id-input]') || {}).value) || 0;
    const plan = Number((f.querySelector('[data-ae4-plan-input]') || {}).value) || 0;
    return id ? { id, selling_plan: plan || null } : null;
  };
  const pseudoOf = (c, line) => ({
    handle: line.id === Number(c && c.adult_variant_id) ? c.adult_handle : line.id === Number(c && c.three_month_variant_id) ? c.three_month_handle : '',
    selling_plan_allocation: line.selling_plan ? { selling_plan: { id: line.selling_plan } } : null,
  });
  function prep() {
    const c = cfg();
    if (!guarding(c) || !ctaSeen) return prepChain;
    prepBusy++;
    prepChain = prepChain.then(async () => {
      if (auditBusy) await audited; // r30b: the page-load audit first, then judge its result
      const line = pickedLine();
      if (!line) return;
      const before = known || (await (prefetching || getCart()).catch(() => null));
      if (!isCart(before)) return;
      const guard = guardCodes(c);
      const codes = (before.discount_codes || []).map((d) => String(d.code).toLowerCase());
      if (codes.some((x) => !guard.includes(x))) return; // another code on the cart: leave it to the add
      const next = wanted(c, before, (before.items || []).concat([pseudoOf(c, line)]));
      if (next === null) return;
      if (foOn(c) && next === String(c.first_order_code)) { // r30b: only the add is once per cart state; a removal always goes
        const key = ['prep', before.token, before.item_count, before.total_price, next].join('|');
        if (tried.has(key)) return;
        tried.add(key);
      } else tried.clear(); // as decide(): picking 1-month again may park the code again
      // r30b: with lines in the cart the (closed) drawer is re-rendered from the same call, so its
      // prices and line keys follow the new codes; never over a drawer that is showing an add
      const sec = before.item_count > 0;
      const u = remember(await post('/cart/update.js', sec ? withSections({ discount: next }) : { discount: next }));
      const d = drawer();
      if (sec && d && !d.classList.contains('is-ae-loading') && !prepBusyAdd) render(sectionHtml(u), Promise.resolve(u));
    }).catch((e) => { console.warn('ae-cart prep', e); }).finally(() => { prepBusy--; });
    return prepChain;
  }
  function watchCta() {
    const els = document.querySelectorAll('[data-ae4-submit], [data-ae4-sticky-cta]');
    if (!els.length || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((ents) => {
      if (!ents.some((e) => e.isIntersecting)) return;
      ctaSeen = true;
      prep();
    });
    els.forEach((el) => io.observe(el));
    document.addEventListener('ae4:plan', () => { if (ctaSeen) prep(); });
  }

  async function addAndOpen(item, opts) {
    opts = opts || {};
    const d = drawer();
    if (!d || !sectionId() || !contentEl()) throw new Error('no cart drawer');
    const c = cfg();
    const line = { id: Number(item.id), quantity: Math.max(1, Number(item.quantity) || 1) };
    if (item.selling_plan) line.selling_plan = Number(item.selling_plan);
    const pseudo = {
      handle: line.id === Number(c && c.adult_variant_id) ? c.adult_handle : line.id === Number(c && c.three_month_variant_id) ? c.three_month_handle : '',
      selling_plan_allocation: line.selling_plan ? { selling_plan: { id: line.selling_plan } } : null,
    };
    const after = (cart) => (cart.items || []).concat([pseudo]);
    const addIt = () => post('/cart/add.js', withSections({ items: [line] }));
    let codesBefore = null;
    /* The first request leaves before the drawer opens (the open costs a style pass), so the
       network and the slide overlap. Code change first when one is due, else the add itself. */
    const codeStep = (before) => {
      const next = before ? wanted(c, before, after(before)) : null;
      if (next === null) return null;
      codesBefore = (before.discount_codes || []).map((x) => x.code).join(',');
      return post('/cart/update.js', { discount: next }).then(async (u) => {
        remember(u);
        // the update returns the cart as it really is: re-check once in case `known` was stale
        const again = wanted(c, u, after(u));
        if (again !== null) remember(await post('/cart/update.js', { discount: again }));
      }).catch((e) => { console.warn('ae-cart code', e); });
    };
    // r29: a code prep still in flight is waited for (it is already on its way), then re-checked
    // (r30b: so is a page-load audit)
    const pending = prepBusy || auditBusy ? Promise.all([prepChain, audited]) : null;
    if (pending) prepBusyAdd = true;
    let first = !pending && known ? codeStep(known) : null;
    let adding = !pending && known && !first ? addIt() : null;
    const bandLikely = !!(foOn(c) && known && foEligible(c, after(known)));
    openLoading(opts.focus, bandLikely);
    try {
      if (pending) { await pending; prepBusyAdd = false; }
      if (!adding && !first) {
        const before = known || (await (prefetching || getCart()).catch(() => null));
        first = codeStep(before);
      }
      if (first) await first;
      const res = await (adding || addIt());
      const cartP = getCart();
      if (!render(sectionHtml(res), cartP)) await refreshDrawer({ aeSynced: true });
      verify(cartP);
      return res;
    } catch (e) {
      if (codesBefore !== null) post('/cart/update.js', { discount: codesBefore }).then(remember).catch(() => {});
      setLoading(false);
      if (d.open && typeof d.hide === 'function') d.hide();
      throw e;
    }
  }

  /* ---- r28: warm-up at idle: known cart, drawer fonts, line images ---- */
  function warm() {
    if (!document.querySelector('[data-ae4-form]')) return; // the PDP buy box (addAndOpen); other pages skip it
    prefetching = getCart().catch(() => null).finally(() => { prefetching = null; });
    watchCta();
    try {
      if (document.fonts && document.fonts.load) ['700 21px Youth', '900 21px Youth'].forEach((f) => document.fonts.load(f).catch(() => {}));
    } catch (e) {}
    const tpl = document.querySelector('template[data-ae-cart-warm]');
    if (tpl && tpl.content) {
      tpl.content.querySelectorAll('img').forEach((src) => {
        const img = new Image();
        img.decoding = 'async';
        if (src.getAttribute('sizes')) img.sizes = src.getAttribute('sizes');
        if (src.getAttribute('srcset')) img.srcset = src.getAttribute('srcset');
        img.src = src.getAttribute('src');
      });
    }
  }
  /* r30b: page load. The drawer's server render lists the cart's lines; when one is an adult 3-month
     line, read the cart once and take a guard code off it (also one the cart holds as not applicable,
     which Shopify applies again as soon as the code is valid). The drawer is closed: nobody sees it. */
  let audited = null;
  let auditBusy = false;
  function audit() {
    const c = cfg();
    const el = contentEl();
    if (!guarding(c) || !el || audited) return audited;
    const p3 = String(c.plan_3m_id || '');
    const hs = [c.adult_handle, c.three_month_handle].map(String);
    const has3m = Array.prototype.some.call(el.querySelectorAll('li[data-plan][data-handle]'),
      (li) => li.getAttribute('data-plan') === p3 && hs.includes(li.getAttribute('data-handle')));
    if (!has3m) return null;
    auditBusy = true;
    audited = getCart().then(async (cart) => {
      const next = decide(cart);
      if (next === null) return;
      const m0 = mutations;
      const u = await post('/cart/update.js', withSections({ discount: next }));
      if (mutations !== m0 + 1) { refresh(false); return; } // another cart call went out meanwhile: re-render from the server
      remember(u);
      if (!render(sectionHtml(u), Promise.resolve(u))) refresh(false);
    }).catch((e) => { console.warn('ae-cart audit', e); }).finally(() => { auditBusy = false; });
    return audited;
  }

  /* r30b: the theme's quantity stepper and trash (assets/cart.js CartItems.updateQuantity ->
     /cart/change.js with sections, then afterUpdate). change.js cannot carry a code, so when the new
     quantities change what the code should be (e.g. the 3-month line goes and a 1-month line stays),
     the code change is sent first and the theme's one render already has the right price.
     afterUpdate still checks the result. Only from a known cart whose line keys match the screen. */
  function preQty(line, quantity) {
    const c = cfg();
    if (!guarding(c) || !isCart(known)) return null;
    const n = Number(line) - 1;
    if (!(n >= 0 && n < known.items.length)) return null;
    const li = document.getElementById('CartDrawer-Item-' + line) || document.getElementById('CartItem-' + line);
    if (!li || li.getAttribute('data-key') !== known.items[n].key) return null;
    const q = Math.max(0, Number(quantity) || 0);
    const items = known.items.map((i, k) => (k === n ? Object.assign({}, i, { quantity: q }) : i)).filter((i) => i.quantity > 0);
    const next = wanted(c, known, items);
    if (next === null) return null;
    if (foOn(c) && next === String(c.first_order_code)) {
      const key = ['qty', known.token, line, q, next].join('|');
      if (tried.has(key)) return null;
      tried.add(key);
    }
    return post('/cart/update.js', { discount: next }).then(remember).catch((e) => { console.warn('ae-cart qty code', e); });
  }
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('cart-items').then(() => {
      const CI = customElements.get('cart-items');
      const proto = CI && CI.prototype;
      if (!proto || proto.__aeQty || typeof proto.updateQuantity !== 'function') return;
      const orig = proto.updateQuantity;
      proto.updateQuantity = function (line, quantity, name, target) {
        // r30b: a drawer 3-month stepper shows plans; Shopify gets units (value x data-ae-plan-step)
        const inp = (target && target.getAttribute ? target : null) || document.getElementById('Quantity-' + sectionId() + '-' + line);
        const per = inp && inp.closest && inp.closest('cart-drawer') ? Math.max(1, parseInt(inp.getAttribute('data-ae-plan-step'), 10) || 1) : 1;
        const units = Math.max(0, Number(quantity) || 0) * per;
        let pre = null;
        try { pre = preQty(line, units); } catch (e) { pre = null; }
        if (!pre) return orig.call(this, line, units, name, target);
        if (typeof this.showLoader === 'function') this.showLoader(line);
        return pre.then(() => orig.call(this, line, units, name, target));
      };
      proto.__aeQty = true;
    }).catch(() => {});
    /* r30b: the theme's cart-count bubbles (header, drawer) take the count in plans from every
       cartUpdate the theme publishes (its own quantity / remove renders included). */
    customElements.whenDefined('cart-count').then(() => {
      const CC = customElements.get('cart-count');
      const proto = CC && CC.prototype;
      if (!proto || proto.__aeCount || typeof proto.onCartUpdate !== 'function') return;
      const orig = proto.onCartUpdate;
      proto.onCartUpdate = function (event) {
        if (event && event.cart) event = Object.assign({}, event, { cart: shownCart(event.cart) });
        return orig.call(this, event);
      };
      proto.__aeCount = true;
    }).catch(() => {});
  }
  /* r30b: page load. The header bubble is the theme's Liquid (cart.item_count, units); the drawer's
     server render carries the count in plans, so the bubbles and the sticky cart button take that. */
  function pageCounts() {
    const n = renderedCount(contentEl());
    if (n === null) return;
    document.querySelectorAll('cart-count').forEach((el) => {
      if (el.closest('cart-drawer')) return;
      const cur = el.hasAttribute('hidden') ? 0 : parseInt(el.textContent, 10);
      if (cur === n) return;
      try { if (typeof el.onCartUpdate === 'function') el.onCartUpdate({ cart: { item_count: n } }); } catch (e) {}
    });
    lastCount = n;
    const fire = () => document.dispatchEvent(new CustomEvent('ae:cart-count', { detail: { count: lastCount } }));
    fire();
    window.addEventListener('load', fire, { once: true }); // listeners that start later (the sticky bar)
  }

  const idle = (fn) => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 2500 }) : setTimeout(fn, 1200));
  const start = () => {
    pageCounts();
    audit();
    watchBands();
    if (document.readyState === 'complete') idle(warm);
    else window.addEventListener('load', () => idle(warm), { once: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('cart:updated', (e) => { if (e.detail && e.detail.cart) remember(e.detail.cart); });
  /* r28: a finger on the PDP buy button: re-read the cart (side-effect free), which also wakes the
     connection if it went idle, so the add that follows starts from a fresh cart on a warm socket. */
  document.addEventListener('pointerdown', (e) => {
    const b = e.target && e.target.closest && e.target.closest('[data-ae4-submit], [data-ae4-sticky-cta]');
    if (!b) return;
    if (!ctaSeen) { ctaSeen = true; prep(); } // r29: a tap before the observer fired (no-op when the codes are right)
    if (Date.now() - lastRead < 8000) return;
    getCart().catch(() => {});
  }, { capture: true, passive: true });

  const showMsg = (el, text) => {
    const box = el && (el.closest('.ae-cart__up, .ae-cart__upsell, .ae-cart__cross') || el.closest('li'));
    const msg = box && box.querySelector('[data-ae-msg]');
    if (msg) { msg.textContent = text; msg.hidden = false; }
  };
  const KEEP = 'That change did not go through. Your item is still in your cart.';

  /* r30b: Shopify gives every line a NEW key whenever the cart's codes change (live 2026-10-02), so a
     key from an older render goes stale after any code change that rendered nothing (prep, a code
     change in another tab). A change by key that Shopify rejects ("no valid id or line parameter")
     is retried once with the key now at that line's position, if it is the same variant. */
  async function freshKey(key) {
    const li = Array.prototype.find.call(document.querySelectorAll('cart-drawer li[data-key]'), (x) => x.getAttribute('data-key') === key);
    const pos = li ? parseInt(String(li.id || '').replace(/^\D+/, ''), 10) : NaN; // CartDrawer-Item-<index + 1>
    if (!(pos > 0)) return null;
    const cart = await getCart();
    const it = cart && cart.items && cart.items[pos - 1];
    return it && String(it.variant_id) === String(li.getAttribute('data-variant-id')) ? it.key : null;
  }
  async function changeLine(key, body) {
    try {
      return await post('/cart/change.js', withSections(Object.assign({ id: key }, body)));
    } catch (e) {
      const msg = String((e.data && (e.data.message || e.data.description)) || e.message || '');
      if (!/no valid id/i.test(msg)) throw e;
      const fresh = await freshKey(key);
      if (!fresh || fresh === key) throw e;
      return post('/cart/change.js', withSections(Object.assign({ id: fresh }, body)));
    }
  }

  /** task() returns the last cart call's response, posted withSections(). */
  async function run(el, task, failText) {
    busy(true);
    try {
      const res = await task();
      let cart = isCart(res) ? remember(res) : null;
      let html = sectionHtml(res);
      try {
        const c = cfg();
        if (guarding(c)) {
          if (!cart) cart = await getCart();
          const next = decide(cart);
          if (next !== null) {
            const u = remember(await post('/cart/update.js', withSections({ discount: next })));
            cart = u;
            html = sectionHtml(u);
          }
        }
      } catch (e) { console.warn('ae-cart sync', e); }
      if (!render(html, cart ? Promise.resolve(cart) : null)) refresh(true);
      else if (cart) counts(cart.item_count, cart);
      else verify(getCart());
    } catch (e) {
      console.warn('ae-cart', e);
      if (el && el.type === 'checkbox') el.checked = !el.checked;
      if (el) el.disabled = false;
      showMsg(el, failText || KEEP);
      // Re-render from the server so the drawer shows the cart as it really is.
      setTimeout(() => refresh(true), 1600);
    } finally {
      busy(false);
    }
  }

  document.addEventListener('change', (e) => {
    const box = e.target.closest && e.target.closest('cart-drawer [data-ae-toggle]');
    if (!box) return;
    box.disabled = true;
    const id = box.dataset.key;
    const quantity = Number(box.dataset.qty) || 1;
    // r29 [data-ae-once]: checked = one-time; else (r21 card) checked = subscribe on data-plan
    const once = box.hasAttribute('data-ae-once');
    const sub = once ? !box.checked : box.checked;
    const selling_plan = sub ? Number(box.dataset.plan) || null : null;
    if (sub && !selling_plan) { box.checked = !box.checked; box.disabled = false; return; }
    run(box, () => changeLine(id, { quantity, selling_plan }));
  });

  document.addEventListener('click', (e) => {
    const sw = e.target.closest && e.target.closest('cart-drawer [data-ae-switch3]');
    if (sw) {
      e.preventDefault();
      const c = cfg();
      if (!c || !c.three_month_ok) return;
      sw.disabled = true;
      const key = sw.dataset.key;
      const quantity = Number(sw.dataset.qty) || Number(c.three_month_qty) || 1;
      const plan3 = Number(c.plan_3m_id);
      // r30b: the 3-month offer is the line's own variant (main variant x3): switch it in place
      // (/cart/change.js rejects a bad change with the line untouched); run() then takes the code off
      // from the change's own cart and renders once: 2 calls, no $107.10 on screen.
      const li = sw.closest('li[data-key]');
      const inPlace = !!li && Number(li.getAttribute('data-variant-id')) === Number(c.three_month_variant_id);
      run(sw, async () => {
        if (inPlace) return changeLine(key, { quantity, selling_plan: plan3 });
        await post('/cart/add.js', { items: [{ id: Number(c.three_month_variant_id), quantity, selling_plan: plan3 }] });
        return changeLine(key, { quantity: 0 });
      }, 'The 3-month plan could not be added. Your current plan is still in your cart.');
      return;
    }
    const back = e.target.closest && e.target.closest('cart-drawer [data-ae-switch1]');
    if (back) {
      e.preventDefault();
      if (back.disabled) return;
      const id = back.dataset.key;
      const quantity = Math.max(1, Number(back.dataset.qty) || 1);
      const selling_plan = Number(back.dataset.plan) || 0;
      if (!id || !selling_plan) return;
      back.disabled = true;
      run(back, () => changeLine(id, { quantity, selling_plan }),
        'The 1-month plan could not be set. Your 3-month plan is still in your cart.');
      return;
    }
    const kids = e.target.closest && e.target.closest('cart-drawer [data-ae-addkids]');
    if (kids) {
      e.preventDefault();
      const c = cfg();
      if (!c) return;
      kids.disabled = true;
      run(kids, () => post('/cart/add.js', withSections({ items: [{ id: Number(c.kids_variant_id), quantity: 1, selling_plan: Number(c.kids_plan_id) }] })),
        'The kids version could not be added. Please try again.');
    }
  });

  // Make sure the first-order code is on the cart before checkout.
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest && e.target.closest('cart-drawer form[data-ae-checkout]');
    if (!form) return;
    const c = cfg();
    if (!guarding(c)) return;
    e.preventDefault();
    const btn = form.querySelector('button');
    if (btn) btn.classList.add('is-busy');
    try { await sync(); } catch (err) { console.warn('ae-cart sync', err); }
    window.location.href = (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.root_url ? FoxTheme.routes.root_url.replace(/\/$/, '') : '') + '/checkout';
  });

  window.AECart = { cfg, sync, afterUpdate, refresh, refreshDrawer, checkBands, addAndOpen, openLoading, render, getCart };
})();
